import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';

import { modelOf } from '../../../../prop-types';
import FormModel from '../../../../models/Form';
import CheckoutStore from '../../../../store/CheckoutStore';
import { Col, Row } from 'reactstrap';
import { invalidFormFields } from '../../../../util/constants';
import FormFieldId from '../../../../types/FormFieldId';
import FormGroupField from '../../../form/FormGroupField';
import { labels } from '../../../../util/formLabels';

const addressFormLabels = {
  [FormFieldId.DELIVERY_STREET_ADDRESS]: labels.deliveryStreetAddress,
  [FormFieldId.DELIVERY_POSTAL_CODE]: labels.deliveryPostalCode,
};

const addressFieldLabels = {
  [FormFieldId.DELIVERY_STREET_ADDRESS]: FormFieldId.DELIVERY_STREET_ADDRESS,
  [FormFieldId.DELIVERY_POSTAL_CODE]: FormFieldId.DELIVERY_POSTAL_CODE,
};

const CheckoutShippingCustomerForm = ({
  checkoutStore,
  onOrderUpdate,
  addressForm,
}) => {
  const { cancelOrder, order } = checkoutStore;
  const { getShippingCustomerField, cancelPickups } = order;
  const debounceTimerRef = useRef(null);

  const updateOrder = ({ fetchPickupPoints }) => {
    onOrderUpdate({ withoutTimer: true, fetchPickupPoints });
  };

  const checkAndTriggerPickupFetch = useCallback(() => {
    // Get all relevant fields that are actually in the form
    const relevantFieldIds = Object.keys(addressFormLabels);
    const fieldsInForm = relevantFieldIds
      .map((fieldId) => {
        const field = addressForm.getField(fieldId);
        return field ? { fieldId, field } : null;
      })
      .filter(Boolean);

    // If no relevant fields are in the form, don't trigger fetch
    if (fieldsInForm.length === 0) {
      return;
    }

    // Validate all relevant fields that exist in the form
    fieldsInForm.forEach(({ fieldId }) => {
      addressForm.validateField(fieldId);
    });

    // Check if all existing relevant fields are valid AND have values
    const allFieldsValid = fieldsInForm.every(({ field }) => {
      return field.valid && field.value && field.value.trim() !== '';
    });

    if (allFieldsValid) {
      updateOrder({ fetchPickupPoints: true });
    }
  }, [addressForm, updateOrder]);

  const handleCustomerFieldChange = useCallback(
    (e, fieldId) => {
      cancelOrder();
      cancelPickups();
      const field = getShippingCustomerField(fieldId).field;
      field.setValue(e.target.value);

      // Debounce validation and pickup point loading
      // This handles browser autofill scenarios where onBlur doesn't fire immediately
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        checkAndTriggerPickupFetch();
      }, 1000); // Wait 1 second after user stops typing
    },
    [
      cancelOrder,
      cancelPickups,
      getShippingCustomerField,
      checkAndTriggerPickupFetch,
    ]
  );

  const handleBlur = useCallback(
    (_, field) => {
      // User has no input, so no update is needed
      if (
        (field.value == null || field.value === '') &&
        field.value === field.previousValue
      ) {
        return;
      }

      // User has no input
      if (field.value !== field.previousValue && field.previousValue == null) {
        field.updatePreviousValue();
        return;
      }

      field.updatePreviousValue();

      // Cancel any pending debounced updates
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Check if BOTH fields are valid and trigger pickup fetch accordingly
      checkAndTriggerPickupFetch();
    },
    [checkAndTriggerPickupFetch]
  );

  const renderField = (field) => {
    const type = field.type.toLowerCase();
    const fieldId = field.id;

    return (
      <Col xs={12} md={6} key={fieldId}>
        <FormGroupField
          field={field}
          fieldName={addressFieldLabels[fieldId]}
          formName={'checkout'}
          floatingLabel
          label={addressFormLabels[fieldId]}
          required={field.required}
          maxLength={field.max_length}
          minLength={field.min_length}
          type={type}
          onChange={(e) => handleCustomerFieldChange(e, fieldId)}
          onBlur={handleBlur}
          onFocus={cancelOrder}
        />
      </Col>
    );
  };

  return (
    <div className="CheckoutShippingCustomerForm">
      <Row>
        {addressForm.getFormFields(invalidFormFields.checkout).map((field) => {
          return renderField(field);
        })}
      </Row>
    </div>
  );
};

CheckoutShippingCustomerForm.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
  addressForm: modelOf(FormModel).isRequired,
};

export default inject('checkoutStore')(observer(CheckoutShippingCustomerForm));
